import moment from "moment";
import React, { useRef } from "react";
import { useReactToPrint } from "react-to-print";
import { Modal, ModalBody, Table } from "reactstrap";
import { IoCloseSharp } from "react-icons/io5";
import { FiRefreshCw, FiPrinter } from "react-icons/fi";
import logo from "../../assets/imgs/pdf-logo.png";
import GeneralButton from "../button/GeneralButton";

const cleanDescription = (raw) => {
  if (!raw && raw !== 0) return "N/A";
  let str = String(raw);
  str = str.replace(/<br\s*\/?>/gi, " ").replace(/<\/p>/gi, "\n");
  str = str.replace(/<[^>]+>/g, " ");
  str = str
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&ndash;/gi, "-")
    .replace(/&mdash;/gi, "-");
  str = str.replace(/[\u00A0\u1680\u180E\u2000-\u200B\u202F\u205F\u3000\uFEFF]/g, " ");
  str = str.replace(/[ \t]+/g, " ");
  return str.trim() || "N/A";
};

const JenneQuotePreviewModal = ({
  modal,
  toggle,
  quoteData,
  poDetails,
  sendToJenne,
  jenneSubmitLoading,
  onRefreshQuote,
  jenneQuoteLoading,
}) => {
  const printSectionRef = useRef(null);

  const handlePrint = useReactToPrint({
    contentRef: printSectionRef,
    documentTitle: `Jenne_Quote_${quoteData?.quoteNumber || poDetails?.po_no || poDetails?.id || "quote"}`,
    pageStyle: `
      @page {
        size: letter portrait;
        margin: 6mm 8mm !important;
      }
      @media print {
        html, body {
          width: 100% !important;
          height: auto !important;
          margin: 0 !important;
          padding: 0 !important;
          background: #fff !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .pdf-container {
          width: 100% !important;
          min-width: 100% !important;
          max-width: 100% !important;
          margin: 0 !important;
          padding: 0 !important;
          border: none !important;
          box-shadow: none !important;
          font-size: 11px !important;
        }
        .pdf-container * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .row-2-col {
          display: flex !important;
          flex-direction: row !important;
          width: 100% !important;
          gap: 16px !important;
        }
        .col-half {
          width: 50% !important;
          flex: 1 1 50% !important;
        }
        .row-3-col {
          display: flex !important;
          flex-direction: row !important;
          width: 100% !important;
          gap: 16px !important;
        }
        .col-third {
          width: 33.333% !important;
          flex: 1 1 33.333% !important;
        }
        table {
          page-break-inside: auto !important;
          width: 100% !important;
        }
        tr {
          page-break-inside: avoid !important;
          page-break-after: auto !important;
        }
        thead {
          display: table-header-group !important;
        }
        .no-print {
          display: none !important;
        }
      }
    `,
  });

  const quoteNumber = quoteData?.quoteNumber || "N/A";
  const carrierCode = quoteData?.carrierCode || "Standard Ground";

  return (
    <Modal
      isOpen={modal}
      toggle={toggle}
      centered
      size="xl"
      style={{ maxWidth: "1280px" }}
    >
      <ModalBody
        className="p-4"
        style={{ backgroundColor: "#f8f9fa", maxHeight: "92vh", overflowY: "auto" }}
      >
        {/* Top Header Bar */}
        <div className="d-flex justify-content-between align-items-center mb-3 px-2">
          <div className="d-flex align-items-center gap-3">
            <h5 className="m-0 fw-bold" style={{ color: "#1a1a2e" }}>
              Jenne Live Quotation Preview
            </h5>
            {quoteNumber !== "N/A" && (
              <span className="badge bg-primary fs-6 px-3 py-2">
                Quote #{quoteNumber}
              </span>
            )}
          </div>
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-outline-primary d-inline-flex align-items-center gap-2 px-3 py-1 fw-semibold rounded-2 bg-white shadow-sm"
              onClick={onRefreshQuote}
              disabled={jenneQuoteLoading || jenneSubmitLoading}
              style={{ fontSize: "13px" }}
            >
              <FiRefreshCw className={jenneQuoteLoading ? "spin" : ""} size={14} />
              <span>{jenneQuoteLoading ? "Refreshing..." : "Re-fetch Quote"}</span>
            </button>
            <IoCloseSharp
              size={28}
              onClick={toggle}
              className="pointer text-muted"
              style={{ cursor: "pointer" }}
            />
          </div>
        </div>

        {/* Identical PO PDF View Container */}
        <div className="d-flex justify-content-center w-100 overflow-auto py-2">
          <section
            ref={printSectionRef}
            className="p-4 bg-white shadow-sm pdf-container"
            style={{
              width: "1200px",
              minWidth: "1200px",
              color: "#333",
              fontSize: "14px",
              border: "1px solid #dee2e6",
              margin: "0 auto",
            }}
          >
            <style>
              {`
                @media print {
                  @page {
                    size: letter portrait;
                    margin: 6mm 8mm;
                  }
                  html, body {
                    width: 100% !important;
                    height: auto !important;
                    background: #fff !important;
                    -webkit-print-color-adjust: exact !important;
                    print-color-adjust: exact !important;
                  }
                  .pdf-container {
                    width: 100% !important;
                    min-width: 100% !important;
                    max-width: 100% !important;
                    margin: 0 !important;
                    padding: 0 !important;
                    border: none !important;
                    box-shadow: none !important;
                    font-size: 11px !important;
                  }
                  .pdf-container * {
                    -webkit-print-color-adjust: exact !important;
                    print-color-adjust: exact !important;
                  }
                  .row-2-col {
                    display: flex !important;
                    flex-direction: row !important;
                    width: 100% !important;
                    gap: 16px !important;
                  }
                  .col-half {
                    width: 50% !important;
                    flex: 1 1 50% !important;
                  }
                  .row-3-col {
                    display: flex !important;
                    flex-direction: row !important;
                    width: 100% !important;
                    gap: 16px !important;
                  }
                  .col-third {
                    width: 33.333% !important;
                    flex: 1 1 33.333% !important;
                  }
                  table {
                    page-break-inside: auto !important;
                    width: 100% !important;
                  }
                  tr {
                    page-break-inside: avoid !important;
                    page-break-after: auto !important;
                  }
                  thead {
                    display: table-header-group !important;
                  }
                  .no-print {
                    display: none !important;
                  }
                }
              `}
            </style>

            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-3">
              <img src={logo} style={{ height: "auto", maxWidth: "240px" }} alt="Tech Micro USA" />
              <div className="text-end">
                <h2 className="mb-1 fw-bold" style={{ color: "#1a3b5c", letterSpacing: "1px", textTransform: "uppercase", fontSize: "28px" }}>PURCHASE ORDER</h2>
                <p className="mb-0 text-muted" style={{ fontSize: "14px" }}>Date: {moment(poDetails?.created_at || poDetails?.date_created || Date.now()).format("MMM DD, YYYY")}</p>
                <p className="mb-0 text-muted" style={{ fontSize: "14px" }}>PO #: {poDetails?.po_no || poDetails?.id || "null"}</p>
              </div>
            </div>

            {/* Row 1: Company Details & Order Information */}
            <div className="row-2-col mb-4" style={{ display: "flex", flexDirection: "row", width: "100%", gap: "16px" }}>
              <div className="col-half" style={{ flex: "1 1 50%", width: "50%" }}>
                <div className="border rounded h-100" style={{ backgroundColor: "#f8f9fa", overflow: "hidden" }}>
                  <h6 className="fw-bold mb-0 text-uppercase p-2" style={{ backgroundColor: "#f0f0f0", color: "#000", fontSize: "13px", letterSpacing: "0.5px" }}>Company Details</h6>
                  <div className="p-3">
                    <p className="mb-1" style={{ fontSize: "14px" }}><strong>Techmicro USA</strong></p>
                    <p className="mb-1" style={{ fontSize: "14px" }}>206 Far Hills Drive</p>
                    <p className="mb-1" style={{ fontSize: "14px" }}>Bolingbrook, IL 60440</p>
                    <p className="mb-1" style={{ fontSize: "14px" }}>Sales No: (630) 526 1800</p>
                    <p className="mb-0" style={{ fontSize: "14px" }}>www.techmicrousa.com</p>
                  </div>
                </div>
              </div>
              <div className="col-half" style={{ flex: "1 1 50%", width: "50%" }}>
                <div className="border rounded h-100" style={{ backgroundColor: "#f8f9fa", overflow: "hidden" }}>
                  <h6 className="fw-bold mb-0 text-uppercase p-2" style={{ backgroundColor: "#f0f0f0", color: "#000", fontSize: "13px", letterSpacing: "0.5px" }}>Order Information</h6>
                  <div className="p-3">
                    <div className="d-flex justify-content-between mb-2 pb-2 border-bottom">
                      <span className="fw-bold" style={{ fontSize: "14px" }}>Vendor ID</span>
                      <span style={{ fontSize: "14px" }}>{poDetails?.vendorId ?? "5"}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2 pb-2 border-bottom">
                      <span className="fw-bold" style={{ fontSize: "14px" }}>PO Number</span>
                      <span style={{ fontSize: "14px" }}>{poDetails?.po_no ?? "null"}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2 pb-2 border-bottom">
                      <span className="fw-bold" style={{ fontSize: "14px" }}>Jenne Quote #</span>
                      <span className="fw-bold text-primary" style={{ fontSize: "14px" }}>#{quoteNumber}</span>
                    </div>
                    {carrierCode && (
                      <div className="d-flex justify-content-between mb-2 pb-2 border-bottom">
                        <span className="fw-bold" style={{ fontSize: "14px" }}>Carrier Code</span>
                        <span style={{ fontSize: "14px" }}>{carrierCode}</span>
                      </div>
                    )}
                    <div className="d-flex justify-content-between mb-2 pb-2 border-bottom">
                      <span className="fw-bold" style={{ fontSize: "14px" }}>Shipping</span>
                      <span style={{ fontSize: "14px" }}>{poDetails?.shippingVia || poDetails?.shipping_lines?.[0]?.method_title || "Ground 5 - 7 Days"}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2 pb-2 border-bottom">
                      <span className="fw-bold" style={{ fontSize: "14px" }}>Payment Terms</span>
                      <span style={{ fontSize: "14px" }}>{poDetails?.vendor?.terms || "Due on Receipt"}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2 pb-2 border-bottom">
                      <span className="fw-bold" style={{ fontSize: "14px" }}>Company</span>
                      <span style={{ fontSize: "14px" }}>{poDetails?.company === "" ? "N/A" : poDetails?.company ?? "FEDEX"}</span>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span className="fw-bold" style={{ fontSize: "14px" }}>Freight Billing Account</span>
                      <span style={{ fontSize: "14px" }}>{poDetails?.freightBillingAccount || "N/A"}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Row 2: Bill To, Ship To, Issued To */}
            <div className="row-3-col mb-4" style={{ display: "flex", flexDirection: "row", width: "100%", gap: "16px" }}>
              <div className="col-third" style={{ flex: "1 1 33.333%", width: "33.333%" }}>
                <div className="border rounded h-100" style={{ overflow: "hidden" }}>
                  <h6 className="fw-bold mb-0 text-uppercase p-2" style={{ backgroundColor: "#f0f0f0", color: "#000", fontSize: "13px", letterSpacing: "0.5px" }}>Bill To</h6>
                  <div className="p-3">
                    <p className="mb-1 fw-bold" style={{ fontSize: "14px" }}>TechMicro USA</p>
                    <p className="mb-1" style={{ fontSize: "14px" }}>206 Far Hills Drive</p>
                    <p className="mb-1" style={{ fontSize: "14px" }}>Bolingbrook, IL 60440</p>
                    <p className="mb-1" style={{ fontSize: "14px" }}>Ph: 630-526-1800</p>
                  </div>
                </div>
              </div>
              <div className="col-third" style={{ flex: "1 1 33.333%", width: "33.333%" }}>
                <div className="border rounded h-100" style={{ overflow: "hidden" }}>
                  <h6 className="fw-bold mb-0 text-uppercase p-2" style={{ backgroundColor: "#f0f0f0", color: "#000", fontSize: "13px", letterSpacing: "0.5px" }}>Ship To</h6>
                  <div className="p-3">
                    <p className="mb-1 fw-bold" style={{ fontSize: "14px" }}>{poDetails?.shipping?.company || "TechMicro USA INC"}</p>
                    <p className="mb-1" style={{ fontSize: "14px" }}>{poDetails?.shipping?.first_name || poDetails?.shipping?.last_name ? `${poDetails?.shipping?.first_name || ""} ${poDetails?.shipping?.last_name || ""}` : "Receiving Department"}</p>
                    <p className="mb-1" style={{ fontSize: "14px" }}>{poDetails?.shipping?.address_1 || "206 Far Hill Drive"}</p>
                    {poDetails?.shipping?.address_2 && <p className="mb-1" style={{ fontSize: "14px" }}>{poDetails?.shipping?.address_2}</p>}
                    <p className="mb-1" style={{ fontSize: "14px" }}>{`${poDetails?.shipping?.city || "Bolingbrook"}, ${poDetails?.shipping?.state || "IL"} ${poDetails?.shipping?.postcode || "60440"}`}</p>
                    <p className="mb-1" style={{ fontSize: "14px" }}>Ph: {poDetails?.shipping?.phone || "Not Available"}</p>
                  </div>
                </div>
              </div>
              <div className="col-third" style={{ flex: "1 1 33.333%", width: "33.333%" }}>
                <div className="border rounded h-100" style={{ overflow: "hidden" }}>
                  <h6 className="fw-bold mb-0 text-uppercase p-2" style={{ backgroundColor: "#f0f0f0", color: "#000", fontSize: "13px", letterSpacing: "0.5px" }}>Issued To</h6>
                  <div className="p-3">
                    <p className="mb-1 fw-bold" style={{ fontSize: "14px" }}>{poDetails?.vendor?.company || poDetails?.vendor?.name || "Jenne Inc."}</p>
                    <p className="mb-1" style={{ fontSize: "14px" }}>{poDetails?.vendor?.address_1 || "33665 Lear Industrial Pkwy"}</p>
                    {poDetails?.vendor?.address_2 && <p className="mb-1" style={{ fontSize: "14px" }}>{poDetails?.vendor?.address_2}</p>}
                    <p className="mb-1" style={{ fontSize: "14px" }}>{`${poDetails?.vendor?.city || "Avon"}, ${poDetails?.vendor?.state || "OH"} ${poDetails?.vendor?.postcode || "44011"}`}</p>
                    <p className="mb-1" style={{ fontSize: "14px" }}>Ph: {poDetails?.vendor?.phone || "800-422-6195"}</p>
                    <p className="mb-1" style={{ fontSize: "14px" }}>{poDetails?.vendor?.email || "sales@jenne.com"}</p>
                    {poDetails?.vendor?.website && <p className="mb-1" style={{ fontSize: "14px" }}>{poDetails?.vendor?.website}</p>}
                  </div>
                </div>
              </div>
            </div>

            {/* Row 3: Items Table */}
            <div className="mb-4">
              <Table bordered responsive hover className="mb-0" style={{ fontSize: "14px", tableLayout: "fixed", width: "100%" }}>
                <thead style={{ backgroundColor: "#f0f0f0", color: "#000" }}>
                  <tr className="text-center">
                    <th className="py-2 fw-bold text-uppercase" style={{ fontSize: "12px", width: "16%" }}>ITEM #</th>
                    <th className="py-2 fw-bold text-uppercase" style={{ fontSize: "12px", width: "14%" }}>PART NO.</th>
                    <th className="py-2 fw-bold text-uppercase" style={{ fontSize: "12px", width: "8%" }}>QTY</th>
                    <th className="py-2 fw-bold text-uppercase text-center" style={{ fontSize: "12px", width: "10%" }}>AVAIL</th>
                    <th className="py-2 fw-bold text-uppercase text-start" style={{ fontSize: "12px", width: "32%" }}>DESCRIPTION</th>
                    <th className="py-2 fw-bold text-uppercase" style={{ fontSize: "12px", width: "10%" }}>PRICE</th>
                    <th className="py-2 fw-bold text-uppercase text-end" style={{ fontSize: "12px", width: "10%" }}>EXT PRICE</th>
                  </tr>
                </thead>
                <tbody>
                  {quoteData?.orderedLines?.length > 0 ? (
                    quoteData.orderedLines.map((line, index) => (
                      <tr className="text-center align-middle" key={index}>
                        <td className="py-2" style={{ wordBreak: "break-all" }}>{line?.partNumber || "-"}</td>
                        <td className="py-2" style={{ wordBreak: "break-all" }}>{line?.partNumber || "-"}</td>
                        <td className="py-2">{line?.quantityOrdered ?? 0}</td>
                        <td className="py-2 text-center">
                          <span className={Number(line?.quantityAvailable || 0) >= Number(line?.quantityOrdered || 0) ? "badge bg-success text-white fw-bold" : "badge bg-warning text-dark fw-bold"}>
                            {line?.quantityAvailable ?? 0}
                          </span>
                        </td>
                        <td className="py-2 text-start" style={{ wordBreak: "break-word", overflowWrap: "break-word", whiteSpace: "normal" }}>
                          {cleanDescription(line?.description || poDetails?.line_items?.[index]?.description)}
                        </td>
                        <td className="py-2">${Number(line?.unitPrice || 0).toFixed(2)}</td>
                        <td className="py-2 text-end fw-bold">${Number(line?.extendedPrice || (line?.unitPrice * line?.quantityOrdered) || 0).toFixed(2)}</td>
                      </tr>
                    ))
                  ) : (
                    poDetails?.line_items?.map((item, index) => (
                      <tr className="text-center align-middle" key={index}>
                        <td className="py-2" style={{ wordBreak: "break-all" }}>{item?.sku || "-"}</td>
                        <td className="py-2" style={{ wordBreak: "break-all" }}>{item?.sku?.slice(4) || item?.sku || "-"}</td>
                        <td className="py-2">{item?.quantity ?? 0}</td>
                        <td className="py-2 text-center">-</td>
                        <td className="py-2 text-start" style={{ wordBreak: "break-word", overflowWrap: "break-word", whiteSpace: "normal" }}>
                          {cleanDescription(item?.description)}
                        </td>
                        <td className="py-2">${Number(item?.price || 0).toFixed(2)}</td>
                        <td className="py-2 text-end fw-bold">${((Number(item?.quantity) || 0) * (Number(item?.price) || 0)).toFixed(2)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>

            {/* Row 4: Notes and Totals */}
            <div className="row-2-col mt-4" style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", width: "100%", gap: "16px" }}>
              <div style={{ flex: "1 1 55%", width: "55%" }}>
                {poDetails?.vendorInstructions && (
                  <div className="border rounded mb-3 bg-light" style={{ overflow: "hidden" }}>
                    <h6 className="fw-bold mb-0 text-uppercase p-2" style={{ backgroundColor: "#f0f0f0", color: "#000", fontSize: "13px" }}>Vendor Notes</h6>
                    <div className="p-3" style={{ fontSize: "13px", wordBreak: "break-word" }}>
                      <p className="mb-0">{poDetails?.vendorInstructions}</p>
                    </div>
                  </div>
                )}
              </div>
              <div style={{ flex: "0 0 340px", width: "340px", marginLeft: "auto" }}>
                <div className="border rounded" style={{ overflow: "hidden" }}>
                  <div className="d-flex justify-content-between p-2 border-bottom bg-light" style={{ fontSize: "14px" }}>
                    <span className="fw-bold">Sub Total:</span>
                    <span>
                      ${Number(quoteData?.quoteSubtotal ?? 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between p-2 border-bottom" style={{ fontSize: "14px" }}>
                    <span>Freight:</span>
                    <span>
                      ${parseFloat(quoteData?.freight ?? 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between p-3" style={{ fontSize: "16px", backgroundColor: "#f0f0f0", color: "#000" }}>
                    <span className="fw-bold text-uppercase">ORDER TOTAL:</span>
                    <span className="fw-bold">
                      ${parseFloat(quoteData?.quoteTotal ?? 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* Modal Action Buttons */}
        <div className="d-flex justify-content-between align-items-center mt-3 px-2">
          <button
            type="button"
            className="btn btn-outline-secondary d-inline-flex align-items-center gap-2 px-3 py-2 fw-semibold"
            style={{ fontSize: "14px", borderRadius: "6px" }}
            onClick={handlePrint}
          >
            <FiPrinter size={16} />
            <span>Print Quote Preview</span>
          </button>

          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary px-3 py-2 fw-semibold"
              style={{ fontSize: "14px", borderRadius: "6px" }}
              onClick={toggle}
              disabled={jenneSubmitLoading}
            >
              Close
            </button>
            <GeneralButton
              title={jenneSubmitLoading ? "Submitting to Jenne..." : "Send to Jenne"}
              className="blue-button"
              width="200px"
              handleClick={sendToJenne}
              loading={jenneSubmitLoading}
              disabled={jenneSubmitLoading || jenneQuoteLoading}
            />
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default JenneQuotePreviewModal;
